/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import {
  ComponentItemFinderDialogConfig,
  componentItemFinderDialogRoute,
  ComponentItemFinderListItem,
  makeComponentItemFinderId,
} from '@gh/builder-shared';
import { getAllNewSelectedEntities } from '@gh/common-shared';
import { emptySelectionModel, SelectionModel, SlicedDataSource } from '@gh/core-data';
import { DialogService, NotificationService, WaitingService } from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';
import { union } from 'lodash';
import { BulkUpdateBusinessService } from '../../resources/bulk-update-business.service';
import { itemComponentSplitByGroups } from '../../resources/bulk-update-item-component-list-item.model';
import { BulkUpdateHousePlanDelete } from '../../resources/bulk-update.model';
import { HousePlanBulkUpdate } from '../../resources/house-plan-bulk-update.model';
import { BulkUpdateAbstractComponent } from '../bulk-update-abstract.component';
import { HousePlanGridDefinition } from '../house-plan-grid-definition.service';
import { ItemComponentDeleteGridDefinition } from '../item-component-delete-grid-definition.service';

@Component({
  selector: 'gh-bulk-update-hp-delete',
  template: require('./bulk-update-hp-delete.component.html'),
  styles: [require('./bulk-update-hp-delete.component.scss')],
  providers: [HousePlanGridDefinition, ItemComponentDeleteGridDefinition],
})
export class BulkUpdateHpDeleteComponent extends BulkUpdateAbstractComponent implements OnInit {

  targetHousePlanData: SlicedDataSource<HousePlanBulkUpdate>;
  deleteItemComponentData: ComponentItemFinderListItem[] = [];
  targetSelectionModel = emptySelectionModel();
  dirty: boolean = false;

  constructor(public housePlanGridDefinition: HousePlanGridDefinition,
              public itemComponentGridDefinition: ItemComponentDeleteGridDefinition,
              private notificationService: NotificationService,
              private waitingService: WaitingService,
              private dialogService: DialogService,
              private businessService: BulkUpdateBusinessService) {
    super();
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.targetHousePlanData = this.businessService.loadHousePlansGridDataSource();
  }

  onSave(): void {
    this.businessService.deleteHpSave(this.getRequestBody()).pipe(
      this.waitingService.operator<any>())
      .subscribe(
        () => this.afterSave(),
        this.notificationService.processAll,
      );
  }

  onAddItemComponent(): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      multiselection: true,
      disableFilterMode: false,
      preselected: this.deleteItemComponentData.map((item) => makeComponentItemFinderId(item.id, item.component)),
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe(
        (componentItem: ComponentItemFinderListItem[]) => {
          this.deleteItemComponentData = union(this.deleteItemComponentData, componentItem);
          this.resetDirty(true);
        },
      );
  }

  onTargetSelectionChanged(model: SelectionModel): void {
    this.resetDirty(true);
    this.targetSelectionModel = model;
  }

  onRemoveItem(itemComponent: ComponentItemFinderListItem): void {
    this.deleteItemComponentData = this.deleteItemComponentData.filter((item) => item !== itemComponent);
  }

  protected getRequestBody(): BulkUpdateHousePlanDelete {
    const { items, components } = itemComponentSplitByGroups(this.deleteItemComponentData);

    return {
      targetHousePlans: this.targetSelectionModel,
      items,
      components,
    };
  }

  protected afterSave(): void {
    this.notificationService.success('msg_bulk_update_initiated_successfully');
    this.deleteItemComponentData = [];
    this.targetSelectionModel = emptySelectionModel();
    this.resetDirty(false);
  }
}
